import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { logger } from '@core/utils';
import type { OrganizationSummary, User } from '../types';

/**
 * localStorage key holding the persisted session (`{ token, user, organizations }`).
 *
 * Namespaced so it can never collide with another app served from the same
 * origin, and versioned by shape: a payload that does not parse into a token +
 * user pair is discarded rather than half-restored.
 */
export const AUTH_STORAGE_KEY = 'afterbooking.auth';

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /**
   * The organizations the caller may act on, as sent alongside the user by
   * `POST /api/auth/login/`.
   *
   * They live NEXT TO the user (never nested inside it) because that is exactly
   * how the backend serializes them — `UserSerializer` is deliberately flat.
   * Persisting them is what lets the app shell render the tenant switcher on the
   * first paint after a reload, before `listOrganizations` has answered.
   */
  organizations: OrganizationSummary[];
  /**
   * Whether the session ended because the server rejected the token (a 401 from
   * `baseQuery`) rather than because the user asked to leave.
   *
   * Deliberately NOT persisted: it explains the redirect the user is living
   * through right now, and a reload should not resurrect the explanation.
   */
  sessionExpired: boolean;
}

/** Payload of {@link logout} — how the session ended. */
export interface LogoutOptions {
  /** `true` when a 401 ended the session (token expired or rotated elsewhere). */
  expired?: boolean;
}

/** Payload of {@link setCredentials} — everything a session needs. */
export interface Credentials {
  user: User;
  token: string;
  organizations?: OrganizationSummary[];
}

/** Shape actually written to localStorage. */
interface PersistedAuth {
  token: string;
  user: User;
  organizations?: OrganizationSummary[];
}

const emptyState = (): AuthState => ({
  user: null,
  token: null,
  isAuthenticated: false,
  organizations: [],
  sessionExpired: false,
});

/**
 * Read the persisted session from localStorage.
 *
 * Never throws: a corrupt payload, a partial write, or storage being
 * unavailable (Safari private mode, blocked cookies) must degrade to "logged
 * out" instead of taking the whole SPA down on boot.
 */
export const loadAuthFromStorage = (): AuthState => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return emptyState();
    }

    const parsed = JSON.parse(raw) as Partial<PersistedAuth> | null;
    if (parsed?.token && parsed.user) {
      return {
        user: parsed.user,
        token: parsed.token,
        isAuthenticated: true,
        // Sessions persisted before the organizations were stored (and any
        // hand-edited payload) simply start with an empty list.
        organizations: parsed.organizations ?? [],
        // Never restored: a rehydrated session is a working one, and the notice
        // belongs to the redirect the user just lived through.
        sessionExpired: false,
      };
    }
  } catch (error) {
    logger.error('Error loading auth from localStorage:', error);
  }

  return emptyState();
};

/**
 * Write the session to localStorage, best effort.
 *
 * Persistence lives in the reducer (rather than in a listener) so the slice is
 * self-contained: mounting it is enough for a session to survive a reload, with
 * no extra wiring to remember in the store.
 */
const persistAuth = (payload: PersistedAuth): void => {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    logger.error('Error saving auth to localStorage:', error);
  }
};

/** Drop the persisted session, best effort. */
const clearPersistedAuth = (): void => {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch (error) {
    logger.error('Error clearing auth from localStorage:', error);
  }
};

/**
 * Auth slice.
 *
 * Rehydrated at module load so the very first render already knows whether
 * there is a session — `ProtectedRoute` must not flash the login screen for an
 * authenticated user. `initializeAuth` re-reads storage on demand (the store
 * dispatches it on boot, and it is what a second tab would use to catch up).
 */
const authSlice = createSlice({
  name: 'auth',
  initialState: loadAuthFromStorage(),
  reducers: {
    initializeAuth: (state) => {
      const loaded = loadAuthFromStorage();
      state.user = loaded.user;
      state.token = loaded.token;
      state.isAuthenticated = loaded.isAuthenticated;
      state.organizations = loaded.organizations;
    },
    setCredentials: (state, action: PayloadAction<Credentials>) => {
      const { user, token, organizations = [] } = action.payload;
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      state.organizations = organizations;
      // A fresh session answers whatever the previous one's expiry was saying.
      state.sessionExpired = false;
      persistAuth({ token, user, organizations });
    },
    logout: (state, action: PayloadAction<LogoutOptions | undefined>) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.organizations = [];
      // `baseQuery` marks the 401 path so the login screen can say why the user
      // is suddenly back there, instead of looking like it dropped them.
      state.sessionExpired = action.payload?.expired === true;
      clearPersistedAuth();
    },
  },
});

export const { initializeAuth, setCredentials, logout } = authSlice.actions;

export default authSlice.reducer;

// Selectors. Typed loosely against the slice shape so they can be reused before
// RootState is constructed (avoids a circular import on the store).
interface WithAuth {
  auth: AuthState;
}

export const selectCurrentUser = (state: WithAuth): User | null => state.auth.user;

export const selectCurrentToken = (state: WithAuth): string | null => state.auth.token;

export const selectIsAuthenticated = (state: WithAuth): boolean => state.auth.isAuthenticated;

/** Whether the last session ended on a 401 rather than a deliberate logout. */
export const selectSessionExpired = (state: WithAuth): boolean => state.auth.sessionExpired;

/**
 * The organizations the current user may act on (empty when logged out).
 *
 * Named for what it returns: these are `OrganizationSummary` rows (tenants the
 * caller can switch between), NOT the `Membership` seat rows that
 * `GET /api/auth/me/` returns under `memberships`.
 */
export const selectOrganizations = (state: WithAuth): OrganizationSummary[] =>
  state.auth.organizations;
